import React from 'react'
import PropTypes from 'prop-types'
import debounce from 'lodash/debounce'

import SliderButton from './SliderButton'
import styles from './SliderControl.css'

class SliderControl extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      stepSize: 0
    }

    this.handleClickSlider = this.handleClickSlider.bind(this)
    this.handleMoveMinValue = this.handleMoveMinValue.bind(this)
    this.handleMoveMaxValue = this.handleMoveMaxValue.bind(this)
    this.updateStepSize = this.updateStepSize.bind(this)
    this.debouncedUpdateStepSize = debounce(this.updateStepSize, 250)
  }

  componentDidMount () {
    this.updateStepSize()
    window.addEventListener('resize', this.debouncedUpdateStepSize)
  }

  componentWillUnmount () {
    window.removeEventListener('resize', this.debouncedUpdateStepSize)
  }

  getValue (screenValue) {
    const { values } = this.props
    const { stepSize } = this.state
    const key = Math.round(screenValue / stepSize)

    return values[key]
  }

  getMinValue (screenValue) {
    const { maxPosition } = this.getPositions()
    const correctedScreenValue = this.fitWithinBounds(0, maxPosition, screenValue)

    return this.getValue(correctedScreenValue)
  }

  getMaxValue (screenValue) {
    const { minPosition } = this.getPositions()
    const correctedScreenValue = this.fitWithinBounds(0, this.barWidth - minPosition, screenValue)

    return this.getValue(this.barWidth - correctedScreenValue)
  }

  getPositions () {
    const { values, minValue, maxValue } = this.props
    const { stepSize } = this.state
    const minPosition = values.indexOf(minValue) * stepSize || 0
    const maxPosition = values.indexOf(maxValue) * stepSize || 0

    return { minPosition, maxPosition }
  }

  render () {
    const { values, minValue, maxValue } = this.props
    const { minPosition, maxPosition } = this.getPositions()
    const barWidth = maxPosition - minPosition

    return (
      <div
        className={styles.container}
      >
        <SliderButton
          style={{ left: `${minPosition}px` }}
          onMove={this.handleMoveMinValue}
          ref={(node) => {
            this.sliderButton = node
          }}
        />
        <SliderButton
          style={{ left: `${maxPosition}px` }}
          onMove={this.handleMoveMaxValue}
        />
        { /* TODO: Improve A11Y and re-enable ESLint */ }
        { /* eslint-disable jsx-a11y/click-events-have-key-events */ }
        <div
          className={styles.bar}
          role="slider"
          tabIndex={-1}
          aria-valuemax={values[values.length - 1]}
          aria-valuemin={values[0]}
          aria-valuenow={{ minValue, maxValue }}
          ref={(node) => {
            this.element = node
          }}
          onClick={this.handleClickSlider}
        >
          { /* eslint-enable */ }
          <div className={styles.activeBar} style={{ left: `${minPosition}px`, width: `${barWidth}px` }} />
          <div className={styles.backgroundBar} />
        </div>
      </div>
    )
  }

  onChange ({ minValue, maxValue }) {
    const newMinValue = minValue !== undefined ? minValue : this.props.minValue
    const newMaxValue = maxValue !== undefined ? maxValue : this.props.maxValue

    if (newMinValue !== this.props.minValue || newMaxValue !== this.props.maxValue) {
      this.props.onChange({
        minValue: newMinValue,
        maxValue: newMaxValue
      })
    }
  }

  handleClickSlider (event) {
    const { minPosition, maxPosition } = this.getPositions()
    const x = event.clientX - this.element.getBoundingClientRect().left
    const distanceToMinValue = Math.abs(minPosition - x)
    const distanceToMaxValue = Math.abs(maxPosition - x)

    if (distanceToMinValue < distanceToMaxValue) {
      this.onChange({ minValue: this.getMinValue(x) })
    } else {
      this.onChange({ maxValue: this.getMaxValue(this.barWidth - x) })
    }
  }

  handleMoveMinValue (mouseX) {
    const x = mouseX - this.element.getBoundingClientRect().left
    this.onChange({ minValue: this.getMinValue(x) })
  }

  handleMoveMaxValue (mouseX) {
    const x = (this.barWidth + this.element.getBoundingClientRect().left) - mouseX
    this.onChange({ maxValue: this.getMaxValue(x) })
  }

  updateStepSize () {
    this.barWidth = this.element.offsetWidth
    this.sliderButtonWidth = this.sliderButton.element.clientWidth
    const stepSize = this.barWidth / (this.props.values.length - 1)
    this.setState({ stepSize })
  }

  fitWithinBounds (minValue, maxValue, value) {
    const { stepSize } = this.state

    // Correct value to fit in bounds, 30 refers to the button width
    let newValue = Math.min(Math.max(minValue, value), maxValue - stepSize - this.sliderButtonWidth)

    // Round value to step size
    newValue = Math.round(newValue / stepSize) * stepSize

    return newValue
  }
}

SliderControl.propTypes = {
  minValue: PropTypes.any.isRequired,
  maxValue: PropTypes.any.isRequired,
  values: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired
}

export default SliderControl
